import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, BACKEND_URL, CATEGORIES, CATEGORY_NAME, fmtDuration, fmtBytes } from "@/lib/api";
import { saveClipToDisk, pickDefaultFolder, clearDefaultFolder, getDefaultFolderName, supportsFSApi, ensureFolderPermission } from "@/lib/saveToDisk";
import { Download, Trash2, Search, Play, X, Film, Video, ThumbsUp, ThumbsDown, FileSpreadsheet, CheckSquare, Square, Sparkles, Pencil, Share2, ChevronDown, ChevronRight, Layers, GripVertical, FolderHeart, Check, Upload, ExternalLink, Loader2, HardDrive, FolderOpen, Scissors, Wand2, Link2, Copy, Building2, Calendar } from "lucide-react";
import ClipUploader from "@/components/ClipUploader";
import AutocompleteInput from "@/components/AutocompleteInput";
import RenameModal from "@/components/RenameModal";
import LibraryFilterSidebar from "@/components/LibraryFilterSidebar";
import { openInCapCut } from "@/lib/openInCapCut";
import {
  ClipCard,
  EditClipModal,
  LazyClipPreview,
  AssignGalleryButton,
  VimeoButton,
  copyClipDirectUrl,
} from "@/components/ClipCard";

export default function Library() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("category") || "all";
  const initialSearch = params.get("search") || "";
  const initialEvent = params.get("event") || "";
  const [category, setCategory] = useState(initialCat);
  const [search, setSearch] = useState(initialSearch);
  const [eventFilter, setEventFilter] = useState(initialEvent);
  const [associationFilter, setAssociationFilter] = useState(params.get("association") || "");
  // Entity filters powered by the left sidebar. Each holds either an empty
  // string ("no filter") or the exact entity name (case-insensitive on the
  // server side). Only one entity per bucket can be active at a time.
  const [riderFilter, setRiderFilter] = useState(params.get("rider") || "");
  const [horseFilter, setHorseFilter] = useState(params.get("horse") || "");
  const [ownerFilter, setOwnerFilter] = useState(params.get("owner") || "");
  const [photographerFilter, setPhotographerFilter] = useState(params.get("photographer") || "");
  const [videographerFilter, setVideographerFilter] = useState(params.get("videographer") || "");
  const [announcerFilter, setAnnouncerFilter] = useState(params.get("announcer") || "");
  const [galleryFilter, setGalleryFilter] = useState(params.get("gallery") || "");
  const [events, setEvents] = useState([]);
  const [clips, setClips] = useState([]);
  const [videosById, setVideosById] = useState({});
  const [loading, setLoading] = useState(true);
  const [previewClip, setPreviewClip] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [defaultFolderName, setDefaultFolderName] = useState(null);
  const [savingToDisk, setSavingToDisk] = useState(false);
  const fetchClipsRef = useRef(0);

  useEffect(() => {
    if (supportsFSApi()) {
      getDefaultFolderName().then(setDefaultFolderName).catch(() => {});
    }
  }, []);

  const handlePickFolder = async () => {
    try {
      const h = await pickDefaultFolder();
      setDefaultFolderName(h.name);
      toast.success(`Default save folder set to "${h.name}"`);
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(e.message || "Failed to pick folder");
    }
  };

  const handleClearFolder = async () => {
    await clearDefaultFolder();
    setDefaultFolderName(null);
    toast.info("Default save folder cleared — future saves use your browser download folder.");
  };

  const handleSaveSelectedToDisk = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    // Grab folder permission ONCE upfront while we're still in the user's
    // gesture context. Subsequent `queryPermission` inside the loop won't
    // need a gesture — this is the fix for "only 1 of N clips saved".
    let dir = null;
    if (supportsFSApi()) {
      dir = await ensureFolderPermission();
      // If the user cancelled the picker or denied permission we fall back
      // to per-clip browser downloads inside the loop.
    }

    setSavingToDisk(true);
    let ok = 0, fail = 0;
    const failures = [];
    for (const id of ids) {
      const clip = clips.find((c) => c.id === id);
      if (!clip) continue;
      const ev = videosById[clip.video_id]?.event_name || videosById[clip.video_id]?.original_name || "";
      try {
        await saveClipToDisk(clip, ev, null, dir);
        ok += 1;
      } catch (e) {
        fail += 1;
        failures.push({ id, msg: (e && e.message) || String(e) });
        console.warn("[save] failed for", id, e);
      }
    }
    setSavingToDisk(false);
    if (fail === 0) toast.success(`Saved ${ok} clip${ok === 1 ? "" : "s"} to disk`);
    else toast.error(`Saved ${ok}, failed ${fail}. First error: ${failures[0]?.msg || "unknown"}`, { duration: 15000 });
  };
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showReelDialog, setShowReelDialog] = useState(false);
  const [reelTitle, setReelTitle] = useState("");
  const [reelAddBrand, setReelAddBrand] = useState(true);
  const [reelTemplateId, setReelTemplateId] = useState("");
  const [reelTemplates, setReelTemplates] = useState([]);
  const [editClip, setEditClip] = useState(null);
  const [reelOrder, setReelOrder] = useState([]);
  const [showUploader, setShowUploader] = useState(false);
  const [renamingGroup, setRenamingGroup] = useState(null); // video_id of group being renamed
  const [renameName, setRenameName] = useState("");
  const [renameDate, setRenameDate] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [renamingClip, setRenamingClip] = useState(null); // { id, sub_label } for the clip-title pencil modal

  const startRename = (g) => {
    setRenamingGroup(g.video_id);
    setRenameName((Object.values(videosById).find((v) => v.id === g.video_id)?.event_name) || "");
    setRenameDate((Object.values(videosById).find((v) => v.id === g.video_id)?.event_date) || "");
  };

  const saveRename = async (videoId) => {
    setRenameSaving(true);
    try {
      const r = await api.patch(`/videos/${videoId}`, {
        event_name: renameName.trim(),
        event_date: renameDate.trim(),
      });
      setVideosById((prev) => ({ ...prev, [videoId]: r.data }));
      // refresh events dropdown
      api.get("/events").then((res) => setEvents(res.data.events || [])).catch(() => {});
      toast.success("Event renamed");
      setRenamingGroup(null);
    } catch (err) {
      toast.error("Rename failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setRenameSaving(false);
    }
  };
  const [groupByEvent, setGroupByEvent] = useState(() => {
    return localStorage.getItem("library_group_by_event") === "1";
  });
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("library_group_by_event", groupByEvent ? "1" : "0");
  }, [groupByEvent]);

  useEffect(() => {
    api.get("/reel-templates").then((r) => setReelTemplates((r.data.templates || []).filter((t) => t.enabled))).catch(() => {});
  }, []);

  const fetchClips = () => {
    setLoading(true);
    const q = {};
    if (category && category !== "all") q.category = category;
    if (search.trim()) q.search = search.trim();
    if (eventFilter) q.event_name = eventFilter;
    if (associationFilter) q.association = associationFilter;
    if (riderFilter) q.rider = riderFilter;
    if (horseFilter) q.horse = horseFilter;
    if (ownerFilter) q.owner = ownerFilter;
    if (photographerFilter) q.photographer = photographerFilter;
    if (videographerFilter) q.videographer = videographerFilter;
    if (announcerFilter) q.announcer = announcerFilter;
    if (galleryFilter) q.gallery_id = galleryFilter;
    // Guard against out-of-order responses when the user is typing fast
    // — each request bumps a token; only the most recent one wins.
    const myToken = ++fetchClipsRef.current;
    api.get("/clips", { params: q })
      .then((r) => {
        if (myToken === fetchClipsRef.current) setClips(r.data);
      })
      .finally(() => {
        if (myToken === fetchClipsRef.current) setLoading(false);
      });
  };

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data.events || [])).catch(() => {});
    api.get("/videos").then((r) => {
      const map = {};
      for (const v of (r.data || [])) map[v.id] = v;
      setVideosById(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchClips();
    if (category === "all") {
      params.delete("category");
    } else {
      params.set("category", category);
    }
    if (search.trim()) {
      params.set("search", search.trim());
    } else {
      params.delete("search");
    }
    if (eventFilter) {
      params.set("event", eventFilter);
    } else {
      params.delete("event");
    }
    // Sync every entity filter into the URL so the sidebar state
    // survives a refresh / share.
    const _syncParam = (key, value) => {
      if (value) params.set(key, value);
      else params.delete(key);
    };
    _syncParam("rider", riderFilter);
    _syncParam("association", associationFilter);
    _syncParam("horse", horseFilter);
    _syncParam("owner", ownerFilter);
    _syncParam("photographer", photographerFilter);
    _syncParam("videographer", videographerFilter);
    _syncParam("announcer", announcerFilter);
    _syncParam("gallery", galleryFilter);
    setParams(params, { replace: true });
    // eslint-disable-next-line
  }, [category, search, eventFilter, associationFilter, riderFilter, horseFilter, ownerFilter, photographerFilter, videographerFilter, announcerFilter, galleryFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchClips();
  };

  const handleDelete = async (clip) => {
    if (!window.confirm("Delete this clip?")) return;
    await api.delete(`/clips/${clip.id}`);
    toast.success("Clip deleted");
    fetchClips();
  };

  const handleFeedback = async (clip, feedback) => {
    const newFb = clip.feedback === feedback ? null : feedback;
    await api.put(`/clips/${clip.id}/feedback`, { feedback: newFb });
    fetchClips();
  };

  // Open the SOURCE video in Studio, scrolled to this clip's range, so the
  // user can quickly cut sub-clips within/around it without re-uploading.
  const handleEditInStudio = (clip) => {
    if (!clip.video_id) {
      toast.error("This clip has no source video");
      return;
    }
    const qs = new URLSearchParams({
      seek: String(clip.start_time ?? 0),
      in: String(clip.start_time ?? 0),
      out: String(clip.end_time ?? 0),
    }).toString();
    navigate(`/studio/${clip.video_id}?${qs}`);
  };

  // Turn a rendered clip into its OWN Studio source. Useful when the source
  // video was deleted or when you want to iterate on just the clip's slice.
  const handleDuplicateAsSource = async (clip) => {
    try {
      const r = await api.post(`/clips/${clip.id}/duplicate-as-source`);
      const newVid = r.data;
      if (!newVid?.id) throw new Error("no video returned");
      toast.success("Clip duplicated as a new Studio source");
      navigate(`/studio/${newVid.id}`);
    } catch (e) {
      toast.error("Duplicate failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleSaveOneToDisk = async (clip) => {
    const ev = videosById[clip.video_id]?.event_name || videosById[clip.video_id]?.original_name || "";
    try {
      const r = await saveClipToDisk(clip, ev);
      toast.success(`Saved ${r.filename}`);
    } catch (e) {
      toast.error("Save failed: " + (e?.message || e));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBuildReel = async () => {
    if (reelOrder.length < 2) {
      toast.error("Pick at least 2 clips");
      return;
    }
    if (!reelTitle.trim()) {
      toast.error("Enter a reel title");
      return;
    }
    try {
      await api.post("/reels", {
        title: reelTitle,
        clip_ids: reelOrder,
        add_brand: reelAddBrand,
        template_id: reelTemplateId || null,
      });
      toast.success("Reel queued — check Reels page for progress");
      setShowReelDialog(false);
      setSelectedIds(new Set());
      setSelectMode(false);
      setReelTitle("");
      setReelOrder([]);
      navigate("/reels");
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const counts = useMemo(() => {
    const map = { all: clips.length };
    CATEGORIES.forEach((c) => { map[c.id] = 0; });
    clips.forEach((c) => { if (map[c.category] != null) map[c.category]++; });
    return map;
  }, [clips]);

  // Group clips by source video. Start from the live videos list so every
  // downloaded event appears as a section — even ones with 0 rendered clips.
  // Each section's title comes from the CURRENT video doc (event_name → filename
  // → fallback) rather than the stale `source_video_name` cached on clips.
  const groupedClips = useMemo(() => {
    if (!groupByEvent) return null;
    const titleFor = (v) =>
      (v.event_name || v.original_name || "Untitled event").trim() || "Untitled event";

    // When the user is actively narrowing the library (search, entity filter,
    // gallery filter), don't seed empty groups from every known video — only
    // show groups that actually contain a matching clip. Prevents cases like
    // searching "kyle" and seeing a wall of unrelated associations/videos.
    const isFiltering = Boolean(
      search.trim() || eventFilter || associationFilter || riderFilter ||
      horseFilter || ownerFilter || photographerFilter || videographerFilter ||
      announcerFilter || galleryFilter,
    );

    const map = new Map();

    // Seed groups from every known video so empty events still show —
    // unless the user is filtering (see above).
    if (!isFiltering) {
      Object.values(videosById).forEach((v) => {
        // Respect category filter: hide videos whose category doesn't match
        if (category !== "all" && v.category && v.category !== category) return;
        map.set(v.id, {
          key: v.id,
          video_id: v.id,
          title: titleFor(v),
          event_date: v.event_date || "",
          clips: [],
          total_duration: 0,
          categories: new Set(v.category ? [v.category] : []),
          video_status: v.status || "",
          compress_status: v.compress_status || "",
        });
      });
    }

    // Inject clips into their groups; create groups for any orphan clips
    for (const c of clips) {
      const key = c.video_id || "_orphan";
      let g = map.get(key);
      if (!g) {
        const v = videosById[key] || {};
        g = {
          key,
          video_id: c.video_id || null,
          title: titleFor(v.event_name ? v : { event_name: c.source_video_name }),
          event_date: v.event_date || "",
          clips: [],
          total_duration: 0,
          categories: new Set(),
          video_status: v.status || "",
          compress_status: v.compress_status || "",
        };
        map.set(key, g);
      }
      g.clips.push(c);
      g.total_duration += c.duration || 0;
      if (c.category) g.categories.add(c.category);
    }

    // Sort: groups with clips first (most clips first), then empty groups
    return Array.from(map.values()).sort((a, b) => {
      if ((a.clips.length > 0) !== (b.clips.length > 0)) {
        return a.clips.length > 0 ? -1 : 1;
      }
      return b.clips.length - a.clips.length;
    });
  }, [clips, groupByEvent, videosById, category, search, eventFilter, associationFilter, riderFilter, horseFilter, ownerFilter, photographerFilter, videographerFilter, announcerFilter, galleryFilter]);

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setAllCollapsed = (collapsed) => {
    if (!groupedClips) return;
    setCollapsedGroups(collapsed ? new Set(groupedClips.map((g) => g.key)) : new Set());
  };

  return (
    <div className="flex" data-testid="library-page">
      <LibraryFilterSidebar
        filters={{
          event: eventFilter,
          association: associationFilter,
          rider: riderFilter,
          horse: horseFilter,
          owner: ownerFilter,
          photographer: photographerFilter,
          videographer: videographerFilter,
          announcer: announcerFilter,
          gallery: galleryFilter,
        }}
        onChange={({ key, value }) => {
          const setters = {
            event: setEventFilter,
            association: setAssociationFilter,
            rider: setRiderFilter,
            horse: setHorseFilter,
            owner: setOwnerFilter,
            photographer: setPhotographerFilter,
            videographer: setVideographerFilter,
            announcer: setAnnouncerFilter,
            gallery: setGalleryFilter,
          };
          setters[key] && setters[key](value);
        }}
        onClearAll={() => {
          setEventFilter("");
          setAssociationFilter("");
          setRiderFilter("");
          setHorseFilter("");
          setOwnerFilter("");
          setPhotographerFilter("");
          setVideographerFilter("");
          setAnnouncerFilter("");
          setGalleryFilter("");
        }}
      />
      <div className="flex-1 min-w-0 px-6 md:px-8 py-10 max-w-[1500px]">
      <div className="flex items-end justify-between gap-6 mb-8">
        <div className="border-l-4 border-orange-600 pl-5">
          <div className="label-tiny mb-2">Library</div>
          <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Training Library</h1>
          <p className="text-stone-400 mt-3">
            Every clip cut becomes a labeled example. Filter by event, search by rider, download for social or highlight reels.
          </p>
        </div>
        <button
          onClick={() => setShowUploader(true)}
          className="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
          data-testid="upload-clips-btn"
        >
          <Upload size={14} /> Upload Clips
        </button>
      </div>

      {/* Filter bar */}
      <div className="mb-8 pb-4 border-b border-stone-800 space-y-3">
        {/* Top row: search + event + actions */}
        <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-2" data-testid="library-search-form">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search rider, horse, performance, owner, association…"
              className="field-input !pl-9 w-full"
              data-testid="search-input"
            />
          </div>
          {events.length > 0 && (() => {
            // Two-part sort so users can find events fast:
            //   • RECENT — top 5 most-recently-uploaded events (from the
            //     server, which sorts by uploaded_at DESC before returning).
            //   • ALL A-Z — every event alphabetically for scan-to-find.
            const recent = events.slice(0, 5);
            const alpha = [...events].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
            return (
              <select
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
                className="field-input !w-auto"
                data-testid="event-filter"
              >
                <option value="">All events ({events.length})</option>
                <optgroup label="Recent">
                  {recent.map((ev) => (
                    <option key={`r-${ev.name}`} value={ev.name}>
                      {ev.needs_event_name ? "⚠ " : ""}{ev.name}{ev.date ? ` · ${ev.date}` : ""} ({ev.clip_count})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="All (A–Z)">
                  {alpha.map((ev) => (
                    <option key={`a-${ev.name}`} value={ev.name}>
                      {ev.needs_event_name ? "⚠ " : ""}{ev.name}{ev.date ? ` · ${ev.date}` : ""} ({ev.clip_count})
                    </option>
                  ))}
                </optgroup>
              </select>
            );
          })()}
          <button type="submit" className="btn-secondary" data-testid="search-btn">
            <Search size={14} className="inline mr-1" /> Search
          </button>
          <a
            href={`${BACKEND_URL}/api/clips/export.csv${category !== "all" ? `?category=${category}` : ""}`}
            className="btn-secondary inline-flex items-center gap-1.5"
            title="Export visible clips as CSV"
            data-testid="csv-export-btn"
          >
            <FileSpreadsheet size={14} /> CSV
          </a>
          <button
            type="button"
            onClick={() => { setSelectMode((s) => !s); setSelectedIds(new Set()); }}
            className={`btn-secondary inline-flex items-center gap-1.5 ${selectMode ? "!text-orange-500 !border-orange-700" : ""}`}
            data-testid="select-mode-toggle"
          >
            {selectMode ? <CheckSquare size={14} /> : <Square size={14} />} Select
          </button>
          <button
            type="button"
            onClick={() => setGroupByEvent((g) => !g)}
            className={`btn-secondary inline-flex items-center gap-1.5 ${groupByEvent ? "!text-orange-500 !border-orange-700" : ""}`}
            title="Group clips by source video / event"
            data-testid="group-by-event-toggle"
          >
            <Layers size={14} /> {groupByEvent ? "Grouped" : "Group by Event"}
          </button>
        </form>

        {/* Save-to-disk folder control (Chromium browsers only) */}
        {supportsFSApi() && (
          <div className="flex items-center gap-2 text-xs text-stone-400" data-testid="save-folder-bar">
            <HardDrive size={12} className="text-stone-500" />
            <span className="uppercase tracking-wider text-[10px] text-stone-500">Default save folder:</span>
            {defaultFolderName ? (
              <>
                <span className="font-mono-tc text-orange-400 truncate max-w-[280px]" title={defaultFolderName} data-testid="save-folder-name">
                  {defaultFolderName}
                </span>
                <button
                  onClick={handlePickFolder}
                  className="btn-secondary !py-1 !px-2 text-[10px] inline-flex items-center gap-1"
                  data-testid="save-folder-change"
                >
                  <FolderOpen size={11} /> Change
                </button>
                <button
                  onClick={handleClearFolder}
                  className="btn-secondary !py-1 !px-2 text-[10px] hover:!text-rose-400"
                  data-testid="save-folder-clear"
                  title="Clear — future saves will use your browser download folder"
                >
                  Clear
                </button>
              </>
            ) : (
              <>
                <span className="text-stone-500 italic">not set — browser download folder used</span>
                <button
                  onClick={handlePickFolder}
                  className="btn-secondary !py-1 !px-2 text-[10px] inline-flex items-center gap-1"
                  data-testid="save-folder-pick"
                >
                  <FolderOpen size={11} /> Pick folder
                </button>
              </>
            )}
          </div>
        )}

        {/* Bottom row: category pills */}
        <div className="flex flex-wrap gap-2" data-testid="library-filters">
          <FilterBtn active={category === "all"} onClick={() => setCategory("all")} count={counts.all} testId="filter-all">
            All
          </FilterBtn>
          {CATEGORIES.map((c) => (
            <FilterBtn
              key={c.id}
              active={category === c.id}
              onClick={() => setCategory(c.id)}
              count={counts[c.id]}
              testId={`filter-${c.id}`}
            >
              {c.name}
            </FilterBtn>
          ))}
        </div>
      </div>

      {selectMode && selectedIds.size > 0 && (
        <div className="bg-stone-900 border border-orange-700 px-4 py-3 mb-4 flex items-center gap-3" data-testid="selection-bar">
          <span className="font-mono-tc text-orange-400 text-sm">{selectedIds.size} selected</span>
          <div className="flex-1" />
          <button
            onClick={() => {
              // Initialize ordering from current selection (preserve click order)
              setReelOrder(Array.from(selectedIds));
              setShowReelDialog(true);
            }}
            disabled={selectedIds.size < 2}
            className="btn-primary inline-flex items-center gap-1.5"
            data-testid="build-reel-btn"
          >
            <Sparkles size={14} /> Build Reel
          </button>
          <button
            onClick={handleSaveSelectedToDisk}
            disabled={savingToDisk}
            className="btn-secondary inline-flex items-center gap-1.5 !border-emerald-700 !text-emerald-400 hover:!bg-emerald-600/10 disabled:opacity-50"
            title={defaultFolderName ? `Save into "${defaultFolderName}"` : "Save via browser downloads"}
            data-testid="bulk-save-to-disk"
          >
            {savingToDisk ? <Loader2 size={14} className="animate-spin" /> : <HardDrive size={14} />}
            {savingToDisk ? "Saving…" : `Save ${selectedIds.size} to Disk`}
          </button>
          <button
            onClick={async () => {
              const ids = Array.from(selectedIds);
              if (!window.confirm(`Send ${ids.length} clip${ids.length === 1 ? "" : "s"} to Vimeo? They'll upload in the background — check progress on the Jobs page.`)) return;
              try {
                const r = await api.post("/clips/vimeo-upload", { clip_ids: ids });
                toast.success(`Queued ${r.data.queued} clip${r.data.queued === 1 ? "" : "s"} for Vimeo upload`);
                if (r.data.skipped?.length) toast.message(`${r.data.skipped.length} skipped (already uploading or duplicate)`);
                setSelectedIds(new Set());
                fetchClips();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed to queue Vimeo upload");
              }
            }}
            className="btn-secondary inline-flex items-center gap-1.5 !border-sky-700 !text-sky-400 hover:!bg-sky-600/10"
            data-testid="bulk-vimeo-upload"
          >
            <Video size={14} /> Send to Vimeo
          </button>
          <button onClick={() => setSelectedIds(new Set())} className="btn-secondary" data-testid="clear-selection">Clear</button>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="text-stone-500 py-20 text-center" data-testid="library-loading">Loading…</div>
      ) : clips.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-16 text-center" data-testid="library-empty">
          <Film size={32} className="mx-auto text-stone-600 mb-3" />
          <p className="text-stone-400">No clips here yet.</p>
          <p className="text-stone-500 text-sm mt-2">Head to the Studio to cut some footage.</p>
        </div>
      ) : groupByEvent ? (
        <>
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs text-stone-500 font-mono-tc">
              {groupedClips.length} event{groupedClips.length !== 1 ? "s" : ""} · {clips.length} clip{clips.length !== 1 ? "s" : ""}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setAllCollapsed(false)}
                className="text-xs text-stone-400 hover:text-orange-400 uppercase tracking-wider"
                data-testid="expand-all-groups"
              >
                Expand all
              </button>
              <span className="text-stone-700">·</span>
              <button
                onClick={() => setAllCollapsed(true)}
                className="text-xs text-stone-400 hover:text-orange-400 uppercase tracking-wider"
                data-testid="collapse-all-groups"
              >
                Collapse all
              </button>
            </div>
          </div>
          <div className="space-y-4" data-testid="grouped-list">
            {groupedClips.map((g) => {
              const collapsed = collapsedGroups.has(g.key);
              const groupClipIds = g.clips.map((c) => c.id);
              const groupAllSelected = selectMode && groupClipIds.every((id) => selectedIds.has(id));
              return (
                <section
                  key={g.key}
                  className="border border-stone-800"
                  data-testid={`event-group-${g.video_id || "orphan"}`}
                >
                  <header className="flex items-center gap-3 px-4 py-3 bg-stone-900/40 hover:bg-stone-900/70 transition-colors cursor-pointer" onClick={() => toggleGroup(g.key)}>
                    {collapsed ? <ChevronRight size={16} className="text-stone-500 shrink-0" /> : <ChevronDown size={16} className="text-orange-500 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="font-display uppercase font-bold text-lg truncate" title={g.title}>{g.title}</div>
                      <div className="text-[11px] text-stone-500 font-mono-tc flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>
                          <span className={g.clips.length ? "text-orange-400" : "text-stone-600"}>{g.clips.length}</span>
                          {" "}clip{g.clips.length !== 1 ? "s" : ""}
                        </span>
                        {g.clips.length > 0 && <span>{fmtDuration(g.total_duration)} total</span>}
                        {g.event_date && <span>{g.event_date}</span>}
                        {Array.from(g.categories).slice(0, 3).map((cat) => (
                          <span key={cat} className="text-stone-400">{CATEGORY_NAME[cat] || cat}</span>
                        ))}
                        {g.clips.length === 0 && (
                          <span className="text-amber-400/80">no clips rendered yet</span>
                        )}
                      </div>
                    </div>
                    {selectMode && g.clips.length > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (groupAllSelected) {
                              groupClipIds.forEach((id) => next.delete(id));
                            } else {
                              groupClipIds.forEach((id) => next.add(id));
                            }
                            return next;
                          });
                        }}
                        className={`btn-secondary !py-1 !px-2 text-[10px] ${groupAllSelected ? "!text-orange-500 !border-orange-700" : ""}`}
                        data-testid={`group-select-all-${g.video_id || "orphan"}`}
                      >
                        {groupAllSelected ? "Deselect all" : "Select all"}
                      </button>
                    )}
                    {g.video_id && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); startRename(g); }}
                          className="btn-secondary !py-1 !px-2 text-[10px] hover:!text-orange-400 inline-flex items-center gap-1"
                          data-testid={`group-rename-${g.video_id}`}
                          title="Rename event / set date"
                        >
                          <Pencil size={11} /> Rename
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/studio/${g.video_id}`); }}
                          className="btn-secondary !py-1 !px-2 text-[10px] hover:!text-orange-400"
                          data-testid={`group-open-studio-${g.video_id}`}
                          title="Open this video in Studio"
                        >
                          Open in Studio
                        </button>
                      </>
                    )}
                  </header>
                  {renamingGroup === g.video_id && (
                    <div className="px-4 py-3 border-b border-stone-800 bg-stone-900/30" data-testid={`group-rename-form-${g.video_id}`} onClick={(e) => e.stopPropagation()}>
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Event Name</div>
                          <input
                            value={renameName}
                            onChange={(e) => setRenameName(e.target.value)}
                            placeholder="e.g. Royal Crown Jackpot · Day 2"
                            className="field-input w-full"
                            autoFocus
                            data-testid={`rename-name-input-${g.video_id}`}
                          />
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Date</div>
                          <input
                            type="date"
                            value={renameDate}
                            onChange={(e) => setRenameDate(e.target.value)}
                            className="field-input"
                            data-testid={`rename-date-input-${g.video_id}`}
                          />
                        </div>
                        <button
                          onClick={() => saveRename(g.video_id)}
                          disabled={renameSaving || !renameName.trim()}
                          className="btn-primary !py-2 text-xs whitespace-nowrap"
                          data-testid={`rename-save-${g.video_id}`}
                        >
                          {renameSaving ? "Saving…" : "Save"}
                        </button>
                        <button
                          onClick={() => setRenamingGroup(null)}
                          className="btn-secondary !py-2 text-xs"
                          data-testid={`rename-cancel-${g.video_id}`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  {!collapsed && (
                    g.clips.length === 0 ? (
                      <div className="p-6 text-center text-xs text-stone-500" data-testid={`group-empty-${g.video_id || "orphan"}`}>
                        No clips have been generated for this event yet.
                        {g.video_id && (
                          <>
                            {" "}<button
                              onClick={() => navigate(`/studio/${g.video_id}`)}
                              className="text-orange-400 hover:text-orange-300 underline"
                            >
                              Open in Studio
                            </button> to create cuts.
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {g.clips.map((clip) => (
                          <ClipCard
                            key={clip.id}
                            clip={clip}
                            selectMode={selectMode}
                            selected={selectedIds.has(clip.id)}
                            onToggleSelect={() => toggleSelect(clip.id)}
                            onPreview={() => setPreviewClip(clip)}
                            onDelete={() => handleDelete(clip)}
                            onFeedback={(fb) => handleFeedback(clip, fb)}
                            onEdit={() => setEditClip(clip)}
                            onSaveToDisk={() => handleSaveOneToDisk(clip)}
                            onEditInStudio={() => handleEditInStudio(clip)}
                            onDuplicateAsSource={() => handleDuplicateAsSource(clip)}
                            onRename={() => setRenamingClip(clip)}
                            onFilterAssociation={(nm) => { setAssociationFilter(nm); setEventFilter(""); }}
                            onFilterEvent={(nm) => { setEventFilter(nm); setAssociationFilter(""); }}
                          />
                        ))}
                      </div>
                    )
                  )}
                </section>
              );
            })}
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="clip-grid">
          {clips.map((clip) => (
            <ClipCard
              key={clip.id}
              clip={clip}
              selectMode={selectMode}
              selected={selectedIds.has(clip.id)}
              onToggleSelect={() => toggleSelect(clip.id)}
              onPreview={() => setPreviewClip(clip)}
              onDelete={() => handleDelete(clip)}
              onFeedback={(fb) => handleFeedback(clip, fb)}
              onEdit={() => setEditClip(clip)}
              onSaveToDisk={() => handleSaveOneToDisk(clip)}
              onEditInStudio={() => handleEditInStudio(clip)}
              onDuplicateAsSource={() => handleDuplicateAsSource(clip)}
              onRename={() => setRenamingClip(clip)}
              onFilterAssociation={(nm) => { setAssociationFilter(nm); setEventFilter(""); }}
              onFilterEvent={(nm) => { setEventFilter(nm); setAssociationFilter(""); }}
            />
          ))}
        </div>
      )}

      {showReelDialog && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" data-testid="reel-dialog">
          <div className="bg-stone-950 border border-orange-700 max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 pb-3 shrink-0">
              <div className="font-display uppercase text-2xl font-bold mb-1">Build Highlight Reel</div>
              <p className="text-stone-400 text-sm">{reelOrder.length} clips · drag to set play order</p>
            </div>
            <div className="px-6 space-y-3 shrink-0">
              <div>
                <div className="label-tiny mb-1">Title</div>
                <AutocompleteInput field="reel_title" value={reelTitle} onChange={setReelTitle} className="field-input" placeholder="Sarah's Best Runs 2026" data-testid="reel-title-input" />
              </div>
              {reelTemplates.length > 0 && (
                <div>
                  <div className="label-tiny mb-1">Template</div>
                  <select value={reelTemplateId} onChange={(e) => setReelTemplateId(e.target.value)} className="field-input" data-testid="reel-template-select">
                    <option value="">No template (clip title cards only)</option>
                    {reelTemplates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-stone-300">
                <input type="checkbox" checked={reelAddBrand} onChange={(e) => setReelAddBrand(e.target.checked)} data-testid="reel-brand-toggle" />
                Add brand watermark from Settings
              </label>
            </div>
            <ReelOrderList
              order={reelOrder}
              clips={clips}
              onReorder={setReelOrder}
            />
            <div className="flex gap-2 p-6 pt-3 shrink-0 border-t border-stone-800">
              <button onClick={() => setShowReelDialog(false)} className="btn-secondary flex-1" data-testid="reel-cancel">Cancel</button>
              <button onClick={handleBuildReel} className="btn-primary flex-1" data-testid="reel-create">Build</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewClip && !selectMode && (
        <PreviewModal
          clip={previewClip}
          onClose={() => setPreviewClip(null)}
          onTrimmed={(updated) => {
            setClips((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
            setPreviewClip(updated);
          }}
        />
      )}

      {/* Clip rename (pencil next to title) */}
      {renamingClip && (
        <RenameModal
          label="Rename Clip"
          currentName={renamingClip.sub_label || ""}
          subtitle="Updates the clip's sub-label — the source cut inherits the same change, so the Library and Studio stay in sync."
          testIdPrefix="clip-rename"
          maxLen={120}
          onSave={async (newName) => {
            await api.patch(`/clips/${renamingClip.id}`, { sub_label: newName });
            setClips((prev) => prev.map((c) => (c.id === renamingClip.id ? { ...c, sub_label: newName } : c)));
            toast.success("Clip renamed");
          }}
          onClose={() => setRenamingClip(null)}
        />
      )}

      {/* Edit clip modal */}
      {editClip && (
        <EditClipModal
          clip={editClip}
          onClose={() => setEditClip(null)}
          onSaved={(updated) => {
            setClips((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
            setEditClip(null);
          }}
        />
      )}

      {showUploader && (
        <ClipUploader
          onClose={() => setShowUploader(false)}
          onUploaded={() => { setShowUploader(false); fetchClips(); }}
        />
      )}
      </div>
    </div>
  );
}

function FilterBtn({ active, onClick, count, children, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-xs uppercase tracking-wider border transition-colors flex items-center gap-2 ${
        active
          ? "bg-orange-700 border-orange-600 text-white"
          : "border-stone-700 text-stone-400 hover:border-stone-500 hover:text-stone-200"
      }`}
      data-testid={testId}
    >
      {children}
      <span className={`font-mono-tc text-[10px] px-1.5 py-0.5 ${active ? "bg-black/30" : "bg-stone-800"}`}>{count || 0}</span>
    </button>
  );
}




function ReelOrderList({ order, clips, onReorder }) {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const clipById = useMemo(() => {
    const m = {};
    clips.forEach((c) => { m[c.id] = c; });
    return m;
  }, [clips]);

  const move = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || toIdx < 0 || toIdx >= order.length) return;
    const next = order.slice();
    const [item] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, item);
    onReorder(next);
  };

  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    if (id !== overId) setOverId(id);
  };
  const onDrop = (id) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === id) { setDragId(null); setOverId(null); return; }
    const fromIdx = order.indexOf(dragId);
    const toIdx = order.indexOf(id);
    move(fromIdx, toIdx);
    setDragId(null);
    setOverId(null);
  };

  return (
    <div className="px-6 py-3 flex-1 min-h-0 overflow-y-auto" data-testid="reel-order-list">
      {order.length === 0 ? (
        <div className="text-stone-500 text-sm text-center py-6">No clips picked.</div>
      ) : (
        <ul className="space-y-1.5">
          {order.map((id, idx) => {
            const c = clipById[id];
            if (!c) return null;
            const isDragging = dragId === id;
            const isOver = overId === id && dragId !== id;
            return (
              <li
                key={id}
                draggable
                onDragStart={onDragStart(id)}
                onDragOver={onDragOver(id)}
                onDrop={onDrop(id)}
                onDragEnd={() => { setDragId(null); setOverId(null); }}
                className={`flex items-center gap-2 px-2 py-1.5 border bg-stone-900/60 cursor-grab active:cursor-grabbing transition-all ${
                  isDragging ? "opacity-40 border-orange-700" :
                  isOver ? "border-orange-600 bg-orange-900/20" : "border-stone-800 hover:border-stone-600"
                }`}
                data-testid={`reel-order-item-${id}`}
              >
                <GripVertical size={14} className="text-stone-500 shrink-0" />
                <div className="font-mono-tc text-xs text-orange-400 w-6 shrink-0">{idx + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-display uppercase truncate">
                    {[c.rider_horse_name, c.horse_name].filter(Boolean).join(" · ") || c.sub_label || c.source_video_name || "Untitled"}
                  </div>
                  <div className="text-[10px] font-mono-tc text-stone-500 truncate">
                    {fmtDuration(c.duration)} · {CATEGORY_NAME[c.category] || c.category}
                    {c.rider_horse_name && c.sub_label && <> · {c.sub_label}</>}
                  </div>
                </div>
                <div className="flex flex-col shrink-0">
                  <button
                    onClick={() => move(idx, idx - 1)}
                    disabled={idx === 0}
                    className="text-stone-500 hover:text-orange-400 disabled:opacity-20 disabled:hover:text-stone-500 px-1"
                    title="Move up"
                    data-testid={`reel-order-up-${id}`}
                  >▲</button>
                  <button
                    onClick={() => move(idx, idx + 1)}
                    disabled={idx === order.length - 1}
                    className="text-stone-500 hover:text-orange-400 disabled:opacity-20 disabled:hover:text-stone-500 px-1"
                    title="Move down"
                    data-testid={`reel-order-down-${id}`}
                  >▼</button>
                </div>
                <button
                  onClick={() => onReorder(order.filter((x) => x !== id))}
                  className="text-stone-500 hover:text-rose-400 px-1 shrink-0"
                  title="Remove from reel"
                  data-testid={`reel-order-remove-${id}`}
                >
                  <X size={12} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}








// ---------------------------------------------------------------- PreviewModal
// Video preview + head/tail trim editor with black-frame auto-detect.

/** Playback controls: skip back / forward + slow-motion / fast-forward speed picker. */
function PlaybackControls({ videoRef }) {
  const [speed, setSpeed] = useState(1);
  const speeds = [0.25, 0.5, 1, 1.5, 2];
  const applySpeed = (s) => {
    setSpeed(s);
    if (videoRef.current) videoRef.current.playbackRate = s;
  };
  const skip = (delta) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min((v.duration || 0), v.currentTime + delta));
  };
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 justify-center bg-stone-950 border-t border-stone-800" data-testid="playback-controls">
      <button onClick={() => skip(-5)} className="btn-secondary !py-1.5 !px-3 text-[11px]" data-testid="skip-back-5">« 5s</button>
      <button onClick={() => skip(-1)} className="btn-secondary !py-1.5 !px-3 text-[11px]" data-testid="skip-back-1">« 1s</button>
      <div className="inline-flex border border-stone-800 overflow-hidden" data-testid="speed-picker">
        {speeds.map((s) => (
          <button
            key={s}
            onClick={() => applySpeed(s)}
            className={`px-2.5 py-1.5 text-[10px] font-mono-tc uppercase tracking-widest ${
              speed === s ? "bg-orange-600 text-white" : "text-stone-400 hover:text-stone-100"
            }`}
            data-testid={`speed-${s}`}
          >
            {s === 1 ? "1×" : `${s}×`}
          </button>
        ))}
      </div>
      <button onClick={() => skip(1)} className="btn-secondary !py-1.5 !px-3 text-[11px]" data-testid="skip-fwd-1">1s »</button>
      <button onClick={() => skip(5)} className="btn-secondary !py-1.5 !px-3 text-[11px]" data-testid="skip-fwd-5">5s »</button>
    </div>
  );
}

function PreviewModal({ clip, onClose, onTrimmed }) {
  const [head, setHead] = useState(0);
  const [tail, setTail] = useState(0);
  const [detecting, setDetecting] = useState(false);
  const [trimming, setTrimming] = useState(false);
  const [showTrim, setShowTrim] = useState(false);
  const videoRef = useRef(null);

  const duration = Number(clip.duration || 0);
  const remaining = Math.max(0, duration - head - tail);
  const canApply = (head > 0 || tail > 0) && remaining >= 1 && !trimming;

  // Seek the underlying <video> to `t` seconds. `play` (default false)
  // decides whether to auto-play from that point — used so dragging a
  // slider just scrubs to a still frame without unexpected playback.
  const seekTo = (t, play = false) => {
    const el = videoRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(duration || t, t));
    try {
      el.currentTime = clamped;
      if (play) {
        el.play().catch(() => {});
      } else {
        el.pause();
      }
    } catch (_) {}
  };

  // Preview the trim boundaries live. Dragging the HEAD slider jumps the
  // video to the first frame that WILL BE KEPT after trimming. Dragging
  // the TAIL slider jumps to the last frame that will be kept.
  const onHeadChange = (v) => {
    setHead(v);
    seekTo(v);
  };
  const onTailChange = (v) => {
    setTail(v);
    seekTo(Math.max(0, duration - v - 0.05));
  };

  // When the user opens the trim panel for the first time, jump the
  // video to the current head position so they see exactly what the
  // clip will start on after applying the trim.
  const toggleTrim = () => {
    setShowTrim((wasOpen) => {
      const opening = !wasOpen;
      if (opening) seekTo(head);
      return opening;
    });
  };

  const autoDetect = async () => {
    setDetecting(true);
    try {
      const r = await api.post(`/clips/${clip.id}/blackdetect`);
      setHead(r.data.head_trim || 0);
      setTail(r.data.tail_trim || 0);
      setShowTrim(true);
      if (!r.data.head_trim && !r.data.tail_trim) {
        toast.info("No leading/trailing black frames detected");
      } else {
        toast.success(
          `Detected · head ${r.data.head_trim.toFixed(1)}s · tail ${r.data.tail_trim.toFixed(1)}s`,
        );
      }
    } catch (err) {
      toast.error("Auto-detect failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setDetecting(false);
    }
  };

  const apply = async () => {
    if (!canApply) return;
    if (!window.confirm(
      `Trim ${head.toFixed(1)}s from start + ${tail.toFixed(1)}s from end?\n\n` +
      `New length: ${remaining.toFixed(1)}s (was ${duration.toFixed(1)}s)\n\n` +
      `This re-renders the clip in place — undo requires re-cutting from source.`
    )) return;
    setTrimming(true);
    try {
      const r = await api.post(`/clips/${clip.id}/trim`, {
        head_trim: head,
        tail_trim: tail,
      });
      toast.success(`Trimmed — new length ${(r.data.duration || 0).toFixed(1)}s`);
      // Refresh the <video> element so it reloads the new mp4 (defeat browser cache)
      if (videoRef.current) {
        videoRef.current.src = `${BACKEND_URL}/api/clips/${clip.id}/stream?v=${Date.now()}`;
      }
      setHead(0);
      setTail(0);
      setShowTrim(false);
      onTrimmed(r.data);
    } catch (err) {
      toast.error("Trim failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setTrimming(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="preview-modal"
    >
      <div className="bg-stone-950 border border-stone-700 max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
          <div className="min-w-0">
            <div className="label-tiny">{CATEGORY_NAME[clip.category]}</div>
            <div className="font-display uppercase text-xl truncate">
              {clip.sub_label || clip.source_video_name}
            </div>
          </div>
          <button onClick={onClose} className="btn-secondary" data-testid="close-preview">
            <X size={14} />
          </button>
        </div>
        <video
          ref={videoRef}
          src={`${BACKEND_URL}/api/clips/${clip.id}/stream`}
          controls
          autoPlay
          playsInline
          className="w-full bg-black"
          data-testid="preview-video"
        />
        <PlaybackControls videoRef={videoRef} />
        <div className="p-4 flex flex-wrap gap-3 items-center text-xs text-stone-400 font-mono-tc">
          <span>{fmtDuration(duration)}</span>
          <span>·</span>
          <span>{fmtBytes(clip.size_bytes)}</span>
          {clip.rider_horse_name && (<><span>·</span><span>{clip.rider_horse_name}</span></>)}
          {clip.trimmed && (
            <span className="text-emerald-400/80 inline-flex items-center gap-1">
              · <Scissors size={10} /> trimmed
            </span>
          )}
          <button
            onClick={toggleTrim}
            className={`btn-secondary ml-auto inline-flex items-center gap-1.5 ${showTrim ? "!border-orange-600 !text-orange-400" : ""}`}
            data-testid="toggle-trim"
          >
            <Scissors size={12} /> Trim
          </button>
          <button
            onClick={() => copyClipDirectUrl(clip.id)}
            className="btn-secondary inline-flex items-center gap-1.5"
            data-testid="modal-copy-url-btn"
            title="Copy direct S3 URL — paste into TikTok / Instagram / YouTube import"
          >
            <Link2 size={13} /> Copy URL
          </button>
          <button
            onClick={() => openInCapCut(clip.id, "clip")}
            className="btn-secondary inline-flex items-center gap-1.5 hover:!text-sky-400"
            data-testid="modal-capcut-btn"
            title="Save the video and open CapCut to edit it"
          >
            <Film size={13} /> CapCut
          </button>
          <a
            href={`${BACKEND_URL}/api/clips/${clip.id}/download`}
            className="btn-primary"
            data-testid="modal-download-btn"
          >
            <Download size={14} className="inline mr-1" /> Download
          </a>
        </div>
        {showTrim && (
          <div className="border-t border-stone-800 p-4 space-y-3" data-testid="trim-editor">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-stone-400 inline-flex items-center gap-2">
                <Scissors size={11} className="text-orange-500" />
                Deadspace trim
              </div>
              <button
                onClick={autoDetect}
                disabled={detecting || trimming}
                className="btn-secondary !py-1 !px-2 text-[10px] inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="auto-detect-black"
              >
                {detecting ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                {detecting ? "Detecting…" : "Auto-detect black frames"}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-tiny block mb-1">
                  Start (head): <span className="text-orange-400 font-mono-tc">{head.toFixed(1)}s</span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, duration - tail - 1)}
                  step={0.1}
                  value={head}
                  onChange={(e) => onHeadChange(Number(e.target.value))}
                  className="w-full accent-orange-500"
                  data-testid="trim-head-slider"
                />
              </div>
              <div>
                <label className="label-tiny block mb-1">
                  End (tail): <span className="text-orange-400 font-mono-tc">{tail.toFixed(1)}s</span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, duration - head - 1)}
                  step={0.1}
                  value={tail}
                  onChange={(e) => onTailChange(Number(e.target.value))}
                  className="w-full accent-orange-500"
                  data-testid="trim-tail-slider"
                />
              </div>
            </div>
            <div className="flex items-center justify-between text-[11px] text-stone-400 font-mono-tc">
              <span>
                New length: <span className="text-emerald-400">{remaining.toFixed(1)}s</span>
                {" "}<span className="text-stone-500">(was {duration.toFixed(1)}s)</span>
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setHead(0); setTail(0); }}
                  disabled={trimming}
                  className="btn-secondary !py-1 !px-2 text-[10px]"
                  data-testid="trim-reset"
                >
                  Reset
                </button>
                <button
                  onClick={apply}
                  disabled={!canApply}
                  className="btn-primary !py-1 !px-3 text-[10px] inline-flex items-center gap-1"
                  data-testid="trim-apply"
                >
                  {trimming ? <Loader2 size={11} className="animate-spin" /> : <Scissors size={11} />}
                  {trimming ? "Re-rendering…" : "Apply & re-render"}
                </button>
              </div>
            </div>
            <div className="text-[10px] text-stone-500 italic">
              Stream-copy is attempted first (fast, seconds). If that fails on non-keyframe boundaries, a re-encode fallback is used automatically.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
